import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, PackagePlus, Truck, Check, X as XIcon, Clock, Pencil, Search, Wallet, ChevronDown, ChevronRight, IndianRupee } from "lucide-react";
import { inr, fmtDate } from "@/utils";
import { toast } from "sonner";
import { usePagination } from "@/components/Pagination";

const genUid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `it_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`);
const EMPTY_ITEM = { material_id: "", material_name: "", category: "", uom: "nos", qty: 1, rate: 0, amount: 0, gst_rate: 18, gst_amount: 0 };
const newItem = () => ({ ...EMPTY_ITEM, _uid: genUid() });
const EMPTY = {
  vendor_id: "", vendor_name: "", project_id: "", date: new Date().toISOString().slice(0, 10),
  delivery_type: "warehouse", warehouse: "Main Warehouse", invoice_number: "",
  items: [newItem()],
  total_tds: 0, discount: 0, transport_charges: 0, other_charges: 0,
  gst_treatment: "exclusive", is_interstate: false,
  payment_status: "unpaid", payment_mode: "bank_transfer",
  notes: "", tower: "", wing: "", floor: "",
};

const GST_RATES = [0, 5, 12, 18, 28];

// ---------- Client-side GST helper (mirrors backend _compute_purchase_totals) ----------
function computeTotals(form) {
  const treatment = form.gst_treatment || "exclusive";
  const interstate = !!form.is_interstate;
  let subtotal = 0, total_gst = 0, total_cgst = 0, total_sgst = 0, total_igst = 0;
  const items = form.items.map((it) => {
    const qty = Number(it.qty) || 0;
    const rate = Number(it.rate) || 0;
    const gst_rate = Number(it.gst_rate) || 0;
    const line = +(qty * rate).toFixed(2);
    let amount, gst_amt;
    if (treatment === "inclusive" && gst_rate > 0) {
      amount = +(line / (1 + gst_rate / 100)).toFixed(2);
      gst_amt = +(line - amount).toFixed(2);
    } else if (treatment === "exempt") {
      amount = line; gst_amt = 0;
    } else {
      amount = line; gst_amt = gst_rate > 0 ? +((line * gst_rate) / 100).toFixed(2) : Number(it.gst_amount) || 0;
    }
    const half = +(gst_amt / 2).toFixed(2);
    const cgst = interstate ? 0 : half;
    const sgst = interstate ? 0 : +(gst_amt - half).toFixed(2);
    const igst = interstate ? gst_amt : 0;
    subtotal += amount; total_gst += gst_amt; total_cgst += cgst; total_sgst += sgst; total_igst += igst;
    return { ...it, amount, gst_amount: gst_amt, cgst_amount: cgst, sgst_amount: sgst, igst_amount: igst };
  });
  const transport = Number(form.transport_charges) || 0;
  const other = Number(form.other_charges) || 0;
  const disc = Number(form.discount) || 0;
  const tds = Number(form.total_tds) || 0;
  const grand = subtotal + total_gst + transport + other - disc + tds;
  return {
    items, subtotal: +subtotal.toFixed(2), total_gst: +total_gst.toFixed(2),
    total_cgst: +total_cgst.toFixed(2), total_sgst: +total_sgst.toFixed(2),
    total_igst: +total_igst.toFixed(2), grand_total: +grand.toFixed(2),
  };
}

export default function Purchases() {
  const qc = useQueryClient();
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const { data: vendors = [] } = useQuery({ queryKey: ["vendors"], queryFn: async () => (await api.get("/vendors")).data });
  const { data: materials = [] } = useQuery({ queryKey: ["materials"], queryFn: async () => (await api.get("/materials")).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const [projectFilter, setProjectFilter] = useState("");
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const { data: purchases = [] } = useQuery({
    queryKey: ["purchases", projectFilter],
    queryFn: async () => (await api.get("/purchases", { params: projectFilter ? { project_id: projectFilter } : {} })).data,
  });

  const filteredPurchases = useMemo(() => {
    if (!search.trim()) return purchases;
    const q = search.toLowerCase();
    return purchases.filter((p) =>
      (p.purchase_number || "").toLowerCase().includes(q) ||
      (p.vendor_name || "").toLowerCase().includes(q) ||
      (p.invoice_number || "").toLowerCase().includes(q) ||
      (p.status || "").toLowerCase().includes(q) ||
      (p.items || []).some((it) => (it.material_name || "").toLowerCase().includes(q))
    );
  }, [purchases, search]);

  const { pageRows, controls } = usePagination(filteredPurchases);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const totals = useMemo(() => computeTotals(form), [form]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- computeTotals is a module-level function, stable across renders

  function openNew() {
    setForm(EMPTY); setEditingId(null); setOpen(true);
  }
  function openEdit(p) {
    setEditingId(p.id);
    setForm({
      ...EMPTY,
      ...p,
      items: (p.items && p.items.length) ? p.items.map((i) => ({ ...EMPTY_ITEM, _uid: genUid(), ...i })) : [newItem()],
      floor: p.floor || "",
    });
    setOpen(true);
  }

  function invalidate() {
    ["purchases", "materials", "expenses", "expense-summary", "stock-movements", "dashboard-kpi", "pnl-page", "vendor-ledger"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  }

  async function approve(id) {
    try { await api.patch(`/purchases/${id}/approve`); toast.success("Purchase approved · stock & expenses updated"); invalidate(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  async function reject(id) {
    const reason = window.prompt("Reason for rejection?", "");
    if (reason === null) return;
    try { await api.patch(`/purchases/${id}/reject`, { reason }); toast.success("Purchase rejected"); invalidate(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  async function del(p) {
    const reason = window.prompt(`Delete purchase ${p.purchase_number}?\nThis will reverse stock & expense entries.\nReason:`, "");
    if (reason === null) return;
    try { await api.delete(`/purchases/${p.id}`, { params: { reason: reason || "Deleted" } }); toast.success("Purchase deleted · stock & expenses reversed"); invalidate(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  function setItem(idx, patch) {
    setForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], ...patch };
      if (patch.material_id) {
        const m = materials.find((x) => x.id === patch.material_id);
        if (m) items[idx] = { ...items[idx], material_name: m.name, category: m.category, uom: m.uom || m.unit || "nos", rate: m.purchase_rate || m.avg_cost || items[idx].rate };
      }
      return { ...f, items };
    });
  }
  function addLine() { setForm((f) => ({ ...f, items: [...f.items, newItem()] })); }
  function delLine(idx) { setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) })); }

  async function save() {
    if (!form.project_id) return toast.error("Select a project/site");
    if (!form.vendor_name) return toast.error("Vendor is required");
    if (!form.items.length || !form.items.some((i) => i.material_name && i.qty)) return toast.error("Add at least one item");
    try {
      const payload = {
        ...form,
        items: form.items.filter((i) => i.material_name && i.qty).map((i) => ({
          ...i, qty: +i.qty, rate: +i.rate, gst_rate: +i.gst_rate, amount: +i.amount || (+i.qty * +i.rate), gst_amount: +i.gst_amount || 0,
        })),
        total_tds: +form.total_tds || 0,
        discount: +form.discount || 0,
        transport_charges: +form.transport_charges || 0,
        other_charges: +form.other_charges || 0,
        floor: form.floor ? +form.floor : null,
      };
      if (editingId) {
        payload._reason = window.prompt("Reason for edit (audit)?", "") || "Edited";
        await api.patch(`/purchases/${editingId}`, payload);
        toast.success("Purchase updated · stock & expenses recalculated");
      } else {
        await api.post("/purchases", payload);
        toast.success("Purchase recorded · inventory & expenses updated");
      }
      invalidate();
      setOpen(false); setForm(EMPTY); setEditingId(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  return (
    <div className="space-y-6" data-testid="purchases-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-emerald-700">Procurement</div>
          <h1 className="font-heading text-3xl font-bold mt-1">Purchases</h1>
          <p className="text-sm text-zinc-500 mt-1">
            One entry updates inventory <b>and</b> project cost <b>and</b> P&amp;L <b>and</b> stock ledger — automatically.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            <Input
              placeholder="Search PO #, vendor, invoice, material…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 w-72 pl-8"
              data-testid="purchase-search"
            />
          </div>
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="w-64" data-testid="purchases-project-filter"><SelectValue placeholder="All projects" /></SelectTrigger>
            <SelectContent>
              <SelectItem value=" ">All projects</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={openNew} className="bg-emerald-700 hover:bg-emerald-800" data-testid="new-purchase-btn"><Plus size={14} className="mr-1" /> New Purchase</Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY); setEditingId(null); } }}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editingId ? "Edit Purchase" : "New Purchase"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="col-span-2"><Label>Vendor</Label>
              <Select value={form.vendor_id} onValueChange={(v) => { const ven = vendors.find((x) => x.id === v); setForm({ ...form, vendor_id: v, vendor_name: ven?.name || "" }); }}>
                <SelectTrigger data-testid="pur-vendor"><SelectValue placeholder="Pick vendor" /></SelectTrigger>
                <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name} · {v.category}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Project / Site</Label>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="pur-project"><SelectValue placeholder="Pick project" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div><Label>Delivery Type</Label>
              <Select value={form.delivery_type} onValueChange={(v) => setForm({ ...form, delivery_type: v })}>
                <SelectTrigger data-testid="pur-delivery"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="warehouse">Warehouse (adds to stock)</SelectItem>
                  <SelectItem value="direct_site">Direct to Site (consumed immediately)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Warehouse</Label><Input value={form.warehouse} onChange={(e) => setForm({ ...form, warehouse: e.target.value })} disabled={form.delivery_type === "direct_site"} /></div>
            <div><Label>Invoice / PO No.</Label><Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="pur-invoice" /></div>
            <div><Label>Tower</Label><Input value={form.tower} onChange={(e) => setForm({ ...form, tower: e.target.value })} /></div>
            <div><Label>GST Treatment</Label>
              <Select value={form.gst_treatment} onValueChange={(v) => setForm({ ...form, gst_treatment: v })}>
                <SelectTrigger data-testid="pur-gst-treatment"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="exclusive">Exclusive of GST</SelectItem>
                  <SelectItem value="inclusive">Inclusive of GST</SelectItem>
                  <SelectItem value="exempt">GST Exempt</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Checkbox id="interstate" checked={form.is_interstate} onCheckedChange={(v) => setForm({ ...form, is_interstate: !!v })} data-testid="pur-interstate" />
              <Label htmlFor="interstate" className="cursor-pointer">Inter-state (IGST) — otherwise CGST+SGST split</Label>
            </div>
          </div>

          <div className="mt-3 border border-zinc-200 rounded-md overflow-hidden">
            <div className="bg-zinc-50 px-3 py-2 flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-widest font-semibold text-zinc-600">Line Items · GST auto-calculated</div>
              <Button size="sm" variant="outline" onClick={addLine} data-testid="pur-add-line"><Plus size={12} className="mr-1" /> Add line</Button>
            </div>
            <Table>
              <TableHeader className="bg-white"><TableRow>
                <TableHead className="w-[30%]">Material</TableHead>
                <TableHead>UoM</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead className="text-right">Taxable</TableHead>
                <TableHead className="text-right">GST %</TableHead>
                <TableHead className="text-right">GST ₹</TableHead>
                <TableHead />
              </TableRow></TableHeader>
              <TableBody>
                {totals.items.map((it, idx) => (
                  <TableRow key={it._uid || `${idx}-fallback`}>
                    <TableCell>
                      {form.delivery_type === "warehouse" ? (
                        <Select value={it.material_id} onValueChange={(v) => setItem(idx, { material_id: v })}>
                          <SelectTrigger className="h-9" data-testid={`pur-item-${idx}-material`}><SelectValue placeholder="Choose material" /></SelectTrigger>
                          <SelectContent>{materials.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} ({m.category})</SelectItem>)}</SelectContent>
                        </Select>
                      ) : (
                        <Input value={it.material_name} onChange={(e) => setItem(idx, { material_name: e.target.value })} placeholder="Material description" data-testid={`pur-item-${idx}-name`} />
                      )}
                    </TableCell>
                    <TableCell><Input className="w-16 h-9" value={it.uom} onChange={(e) => setItem(idx, { uom: e.target.value })} /></TableCell>
                    <TableCell className="text-right"><Input type="number" className="h-9 text-right" value={it.qty} onChange={(e) => setItem(idx, { qty: e.target.value })} data-testid={`pur-item-${idx}-qty`} /></TableCell>
                    <TableCell className="text-right"><Input type="number" className="h-9 text-right" value={it.rate} onChange={(e) => setItem(idx, { rate: e.target.value })} data-testid={`pur-item-${idx}-rate`} /></TableCell>
                    <TableCell className="text-right font-mono">{inr(it.amount)}</TableCell>
                    <TableCell className="text-right">
                      <Select value={String(it.gst_rate)} onValueChange={(v) => setItem(idx, { gst_rate: +v })}>
                        <SelectTrigger className="h-9 w-20" data-testid={`pur-item-${idx}-gst-rate`}><SelectValue /></SelectTrigger>
                        <SelectContent>{GST_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs text-zinc-600">{inr(it.gst_amount)}</TableCell>
                    <TableCell><Button size="sm" variant="ghost" onClick={() => delLine(idx)} className="text-red-600"><Trash2 size={14} /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="bg-zinc-950 text-white px-4 py-3 font-mono text-xs space-y-1">
              <div className="flex justify-between"><span className="opacity-70">Taxable (Subtotal)</span><span data-testid="pur-subtotal">{inr(totals.subtotal)}</span></div>
              {form.is_interstate ? (
                <div className="flex justify-between"><span className="opacity-70">IGST</span><span data-testid="pur-igst">+ {inr(totals.total_igst)}</span></div>
              ) : (
                <>
                  <div className="flex justify-between"><span className="opacity-70">CGST</span><span data-testid="pur-cgst">+ {inr(totals.total_cgst)}</span></div>
                  <div className="flex justify-between"><span className="opacity-70">SGST</span><span data-testid="pur-sgst">+ {inr(totals.total_sgst)}</span></div>
                </>
              )}
              <div className="flex justify-between border-t border-white/10 pt-1"><span className="opacity-70">Total GST</span><span>+ {inr(totals.total_gst)}</span></div>
              <div className="flex justify-between items-center gap-2"><span className="opacity-70">Transport</span>
                <Input type="number" value={form.transport_charges} onChange={(e) => setForm({ ...form, transport_charges: e.target.value })} className="w-32 h-7 bg-white/10 text-right border-white/20 text-white" />
              </div>
              <div className="flex justify-between items-center gap-2"><span className="opacity-70">Other Charges</span>
                <Input type="number" value={form.other_charges} onChange={(e) => setForm({ ...form, other_charges: e.target.value })} className="w-32 h-7 bg-white/10 text-right border-white/20 text-white" />
              </div>
              <div className="flex justify-between items-center gap-2"><span className="opacity-70">Discount</span>
                <Input type="number" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} className="w-32 h-7 bg-white/10 text-right border-white/20 text-white" />
              </div>
              <div className="flex justify-between items-center gap-2"><span className="opacity-70">TDS</span>
                <Input type="number" value={form.total_tds} onChange={(e) => setForm({ ...form, total_tds: e.target.value })} className="w-32 h-7 bg-white/10 text-right border-white/20 text-white" />
              </div>
              <div className="flex justify-between border-t border-white/20 pt-2 mt-2 text-emerald-300 font-semibold text-sm"><span>Grand Total</span><span data-testid="pur-grand">{inr(totals.grand_total)}</span></div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-3">
            <div><Label>Payment Status</Label>
              <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="unpaid">Unpaid</SelectItem><SelectItem value="partial">Partial</SelectItem><SelectItem value="paid">Paid</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Payment Mode</Label>
              <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem><SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem><SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="rtgs">RTGS/NEFT</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>

          <DialogFooter><Button onClick={save} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-purchase">{editingId ? "Save changes" : "Save · update stock & expenses"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="bg-white border-zinc-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-zinc-50"><TableRow>
              <TableHead className="w-8" />
              <TableHead>Purchase #</TableHead><TableHead>Date</TableHead><TableHead>Vendor</TableHead>
              <TableHead>Type</TableHead><TableHead>Items</TableHead>
              <TableHead className="text-right">Grand Total</TableHead>
              <TableHead className="text-right">Paid / Outstanding</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {pageRows.map((p) => {
                const status = p.status || "approved";
                const statusCls = status === "approved" ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                  : status === "pending_approval" ? "bg-amber-50 border-amber-200 text-amber-700"
                  : "bg-red-50 border-red-200 text-red-700";
                const StatusIcon = status === "approved" ? Check : status === "pending_approval" ? Clock : XIcon;
                const isExpanded = expandedId === p.id;
                return (
                  <React.Fragment key={p.id}>
                    <TableRow className="data-row" data-testid={`purchase-row-${p.purchase_number}`}>
                      <TableCell className="pr-0">
                        <button
                          onClick={() => setExpandedId(isExpanded ? null : p.id)}
                          className="p-1 rounded hover:bg-zinc-100 text-zinc-600"
                          title={isExpanded ? "Hide payments" : "Show payments"}
                          data-testid={`toggle-payments-${p.purchase_number}`}
                        >
                          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                      </TableCell>
                      <TableCell className="font-mono text-xs font-semibold">{p.purchase_number}</TableCell>
                      <TableCell className="text-xs">{fmtDate(p.date)}</TableCell>
                      <TableCell>{p.vendor_name}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${p.delivery_type === "warehouse" ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-orange-50 border-orange-200 text-orange-700"}`}>
                          {p.delivery_type === "warehouse" ? <PackagePlus size={11} /> : <Truck size={11} />}
                          {p.delivery_type === "warehouse" ? "Warehouse" : "Direct Site"}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs">{p.items?.length || 0} · {p.items?.[0]?.material_name}{p.items?.length > 1 ? "…" : ""}</TableCell>
                      <TableCell className="text-right font-mono">{inr(p.grand_total)}</TableCell>
                      <TableCell className="text-right">
                        <PurchasePaidSummary purchase={p} />
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${statusCls}`}>
                          <StatusIcon size={11} /> {status.replace("_", " ")}
                        </span>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {status === "pending_approval" && (
                          <>
                            <Button size="sm" variant="outline" className="mr-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => approve(p.id)} data-testid={`approve-${p.purchase_number}`}>
                              <Check size={12} className="mr-1" /> Approve
                            </Button>
                            <Button size="sm" variant="outline" className="mr-1 border-red-200 text-red-600 hover:bg-red-50" onClick={() => reject(p.id)} data-testid={`reject-${p.purchase_number}`}>
                              <XIcon size={12} />
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="outline" className="mr-1" onClick={() => openEdit(p)} data-testid={`edit-purchase-${p.purchase_number}`}>
                          <Pencil size={12} />
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => del(p)} data-testid={`delete-purchase-${p.purchase_number}`}>
                          <Trash2 size={12} />
                        </Button>
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow className="bg-zinc-50/60 hover:bg-zinc-50/60">
                        <TableCell colSpan={10} className="p-0">
                          <PurchasePayments purchase={p} onChange={invalidate} />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
              {!purchases.length && <TableRow><TableCell colSpan={10} className="text-center text-sm text-zinc-500 py-16">No purchases yet. Every purchase auto-updates inventory + expenses + P&amp;L.</TableCell></TableRow>}
            </TableBody>
          </Table>
          {controls}
        </CardContent>
      </Card>

      {settings && (
        <div className="text-xs text-zinc-500 font-mono">
          Approval threshold: purchases ≥ <b>{inr(settings.po_approval_threshold || 100000)}</b> require Admin / Manager approval.
        </div>
      )}
    </div>
  );
}


// ============ Purchase Payments (multi-payment ledger, inline) ============
const PAY_MODES = [
  { v: "bank_transfer", l: "Bank Transfer" },
  { v: "cheque", l: "Cheque" },
  { v: "cash", l: "Cash" },
  { v: "upi", l: "UPI" },
  { v: "rtgs", l: "RTGS/NEFT" },
];

function usePurchasePayments(purchase) {
  return useQuery({
    queryKey: ["purchase-payments", purchase.id],
    queryFn: async () => (await api.get("/vendor-payments", { params: { linked_purchase_id: purchase.id } })).data,
    enabled: !!purchase.id,
  });
}

function PurchasePaidSummary({ purchase }) {
  const { data: payments = [] } = usePurchasePayments(purchase);
  const paid = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const grand = Number(purchase.grand_total) || 0;
  const outstanding = Math.max(grand - paid, 0);
  const fullyPaid = grand > 0 && paid >= grand - 0.5;
  return (
    <div className="text-right font-mono text-xs leading-tight" data-testid={`paid-summary-${purchase.purchase_number}`}>
      <div className={fullyPaid ? "text-emerald-700 font-semibold" : "text-emerald-700"}>{inr(paid)}</div>
      <div className={outstanding > 0 ? "text-red-600" : "text-zinc-400"}>{outstanding > 0 ? `− ${inr(outstanding)}` : "settled"}</div>
    </div>
  );
}

function PurchasePayments({ purchase, onChange }) {
  const qc = useQueryClient();
  const { data: payments = [] } = usePurchasePayments(purchase);
  const [form, setForm] = useState({
    amount: "", date: new Date().toISOString().slice(0, 10),
    payment_mode: "bank_transfer", reference_number: "", notes: "",
  });
  const [busy, setBusy] = useState(false);

  const paid = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const grand = Number(purchase.grand_total) || 0;
  const outstanding = Math.max(grand - paid, 0);

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["purchase-payments", purchase.id] });
    qc.invalidateQueries({ queryKey: ["vendor-ledger"] });
    onChange?.();
  }

  async function addPayment() {
    const amt = Number(form.amount) || 0;
    if (amt <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      await api.post("/vendor-payments", {
        vendor_id: purchase.vendor_id,
        vendor_name: purchase.vendor_name,
        project_id: purchase.project_id,
        amount: amt,
        date: form.date,
        payment_mode: form.payment_mode,
        reference_number: form.reference_number || null,
        linked_purchase_id: purchase.id,
        notes: form.notes || `Payment for ${purchase.purchase_number}`,
      });
      toast.success(`Payment ${inr(amt)} recorded`);
      setForm({ ...form, amount: "", reference_number: "", notes: "" });
      invalidate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to record payment");
    } finally { setBusy(false); }
  }

  async function delPayment(id) {
    if (!window.confirm("Delete this payment entry?")) return;
    try {
      await api.delete(`/vendor-payments/${id}`);
      toast.success("Payment deleted");
      invalidate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  }

  function payFullOutstanding() {
    setForm({ ...form, amount: outstanding.toFixed(2) });
  }

  return (
    <div className="px-4 py-3 border-t border-zinc-200" data-testid={`payments-panel-${purchase.purchase_number}`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-600 font-semibold">
          <Wallet size={13} /> Payment History
          <span className="font-mono normal-case text-zinc-500">· {payments.length} entr{payments.length === 1 ? "y" : "ies"}</span>
        </div>
        <div className="flex items-center gap-3 text-xs font-mono">
          <span>Grand <b>{inr(grand)}</b></span>
          <span className="text-emerald-700">Paid <b>{inr(paid)}</b></span>
          <span className={outstanding > 0 ? "text-red-600" : "text-zinc-400"}>
            Outstanding <b>{inr(outstanding)}</b>
          </span>
        </div>
      </div>

      {/* Payments list */}
      <div className="rounded border border-zinc-200 overflow-hidden mb-3 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-zinc-50">
            <tr className="text-left text-zinc-500 uppercase tracking-widest text-[10px]">
              <th className="px-3 py-1.5">Date</th>
              <th className="px-3 py-1.5">Mode</th>
              <th className="px-3 py-1.5">Reference</th>
              <th className="px-3 py-1.5">Notes</th>
              <th className="px-3 py-1.5 text-right">Amount</th>
              <th className="px-3 py-1.5 w-8" />
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id} className="border-t border-zinc-100" data-testid={`payment-row-${p.id}`}>
                <td className="px-3 py-1.5 font-mono">{fmtDate(p.date)}</td>
                <td className="px-3 py-1.5">{(p.payment_mode || "").replace("_", " ")}</td>
                <td className="px-3 py-1.5 font-mono text-zinc-500">{p.reference_number || "—"}</td>
                <td className="px-3 py-1.5 text-zinc-500 truncate max-w-[200px]">{p.notes || "—"}</td>
                <td className="px-3 py-1.5 text-right font-mono text-emerald-700">{inr(p.amount)}</td>
                <td className="px-3 py-1.5 text-right">
                  <button
                    onClick={() => delPayment(p.id)}
                    className="text-red-500 hover:text-red-700 p-1"
                    title="Delete payment"
                    data-testid={`del-payment-${p.id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </td>
              </tr>
            ))}
            {!payments.length && (
              <tr><td colSpan={6} className="px-3 py-3 text-center text-zinc-400 italic">No payments recorded yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add payment inline form */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end bg-white border border-emerald-200 rounded p-2">
        <div className="md:col-span-1">
          <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Amount ₹</Label>
          <div className="relative">
            <IndianRupee size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            <Input
              type="number"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="h-8 pl-6 text-xs"
              placeholder="0"
              data-testid={`add-payment-amount-${purchase.purchase_number}`}
            />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Date</Label>
          <Input
            type="date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            className="h-8 text-xs"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Mode</Label>
          <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
            <SelectTrigger className="h-8 text-xs" data-testid={`add-payment-mode-${purchase.purchase_number}`}><SelectValue /></SelectTrigger>
            <SelectContent>{PAY_MODES.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Reference #</Label>
          <Input
            value={form.reference_number}
            onChange={(e) => setForm({ ...form, reference_number: e.target.value })}
            className="h-8 text-xs"
            placeholder="Cheque / UTR"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Notes</Label>
          <Input
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="h-8 text-xs"
            placeholder="Optional"
          />
        </div>
        <div className="flex gap-1">
          {outstanding > 0 && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 text-[11px] px-2"
              onClick={payFullOutstanding}
              title={`Pay outstanding ${inr(outstanding)}`}
              data-testid={`pay-full-${purchase.purchase_number}`}
            >
              Full
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            className="h-8 bg-emerald-700 hover:bg-emerald-800 text-white"
            disabled={busy}
            onClick={addPayment}
            data-testid={`add-payment-btn-${purchase.purchase_number}`}
          >
            <Plus size={12} className="mr-1" /> {busy ? "…" : "Add Payment"}
          </Button>
        </div>
      </div>
    </div>
  );
}
